import { Fragment } from "react";
import { Link } from "react-router-dom";
import * as utils from "common/utils";
import * as icons from "common/icons";
import * as domain from "common/domain";
import { useConfirm } from "common/hooks";
import { useAnalysisSummary } from "containers/BudgetHome";
import { WriteMode } from "components/AnalysisRenderMode";
import TileSubPanel from "components/TileSubPanel";
import Alert from "components/Alert";
import ActionLink from "components/ActionLink";
import { HelpTooltip } from "components/Help";
import { useUpdateAnalysisAsync } from "containers/hooks";

export default function IrrigationSystemsGreenBox({ farm, analysis }) {
    const { isFetching, isLoading, irrigation } = useIrrigationSummary(analysis);
    const confirm = useConfirm();
    const updateAnalysisAsync = useUpdateAnalysisAsync(analysis);

    const hasData = !!irrigation;

    const deleteIrrigator = (irrigatorId) => (e) => {
        utils.stopPropagation(e);
        confirm(`Are you sure you want to delete this irrigator?`, async () => {
            analysis.irrigators = analysis.irrigators.filter((i) => i.id !== irrigatorId);
            if (analysis.features) {
                analysis.features = analysis.features.filter((f) => !f.properties.irrigatorId || f.properties.irrigatorId !== irrigatorId);
            }
            await updateAnalysisAsync(analysis);
        });
    };

    const irrigatedAreaHelpTip = irrigation?.byIrrigationSystem?.length > 0 ? irrigation.byIrrigationSystem[0].irrigatedArea.description : undefined;

    return (
        <>
            <TileSubPanel title="Irrigation systems" waiting={isFetching} loading={isLoading} green stickyHeaderName="analysis.irrigation.greenBox.collapsed">
                <Alert type="info" text="The following is a list of irrigation systems for the farm. For pasture blocks that have fodder crop rotations, the irrigated area is adjusted to remove the fodder crop rotation area. The area removed is based on dividing the total fodder area by the total pastoral area for the farm. For crop blocks, the irrigated area is adjusted to only account for the cultivated proportion of the crop block." />
                <div className="FarmTable_wrapper">
                    <div className="FarmTable-supplementary u-pt-md">
                        {hasData ? (
                            <>
                                <div className="Table">
                                    <table className="Calendar">
                                        <thead>
                                            <tr className="Calendar-months">
                                                <th className="Calender-title">Name</th>
                                                <th className="Calendar-month">
                                                    Area covered (ha)
                                                    <HelpTooltip tip={irrigatedAreaHelpTip} />
                                                </th>
                                                {domain.farmYear.map((month) => (
                                                    <th key={month} className="Calendar-month">
                                                        {utils.month.format(month)}
                                                    </th>
                                                ))}
                                                <WriteMode>
                                                    <th className="th--shrink"></th>
                                                </WriteMode>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {irrigation.byIrrigationSystem.map((irrigationSystem) => {
                                                const irrigator = analysis.irrigators.find((e) => e.id === irrigationSystem.id);
                                                const irrigatorIcon = utils.getIrrigatorIcon(irrigator.type);
                                                const irrigatorKey = `irrigator_${irrigationSystem.id}`;
                                                const hasError = analysis.messages?.some((m) => m.entityType === "Irrigator" && m.entityId === irrigationSystem.id && m.severity === "Error");
                                                const hasWarning = !hasError && analysis.messages?.some((m) => m.entityType === "Irrigator" && m.entityId === irrigationSystem.id && m.severity === "Warning");
                                                const alertClass = hasError ? "error" : (hasWarning ? "warning" : undefined);
                                                return (
                                                    <Fragment key={irrigatorKey}>
                                                        <tr key={irrigatorKey} className="hover">
                                                            <td className="td--keyValuePair">
                                                                <span className="u-flex u-flexAlignItemsCenter">
                                                                    {alertClass && (
                                                                        <span className={`Todo Todo--${alertClass}`}>
                                                                            <i className="icon icon-alert" />
                                                                        </span>
                                                                    )}
                                                                    <Link id={`${irrigatorKey}_name`} to={`/app/farm/${farm.id}/analysis/${analysis.id}/irrigation/${irrigationSystem.id}`} className="u-textBold">
                                                                        {irrigationSystem.name}
                                                                    </Link>
                                                                </span>
                                                                <span data-test-id={`${irrigatorKey}_type`}>
                                                                    {irrigationSystem.type}
                                                                </span>
                                                            </td>
                                                            <td className="Calendar-slot">
                                                                {utils.numeric.format(irrigationSystem.irrigatedArea.value, 1)} <small>{irrigationSystem.irrigatedArea?.unit?.notation}</small>
                                                            </td>
                                                            {irrigationSystem.applicationsByMonth.map((m) => {
                                                                const monthKey = `${irrigatorKey}_${m.month}`;
                                                                return (
                                                                    <td key={monthKey} id={monthKey} className="Calendar-slot">
                                                                        {m.isApplied
                                                                            ? <img src={irrigatorIcon} alt={irrigationSystem.type} height="45" width="45" className="Calendar-icon u-p-xxs" />
                                                                            : undefined}
                                                                    </td>
                                                                )
                                                            })}
                                                            <WriteMode>
                                                                <td>
                                                                    <ActionLink className="IconLink--trash" id={`delete-irrigator-${irrigationSystem.id}`} onClick={deleteIrrigator(irrigationSystem.id)} title="Delete" disabled={isFetching}></ActionLink>
                                                                </td>
                                                            </WriteMode>
                                                        </tr>
                                                    </Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="Tile-body">
                                    <div className="Tile-body-message">
                                        <img src={icons.add} className="u-p-5" alt="Plus" />
                                        <p className="lead">This analysis does not have any irrigation systems</p>
                                        <WriteMode>
                                            <Link to={`/app/farm/${farm.id}/analysis/${analysis.id}/irrigation/new`} className="IconLink--arrow-plus Button Button--lg u-mt-lg" id="add-irrigator-button">
                                                Add irrigation system
                                            </Link>
                                        </WriteMode>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </TileSubPanel>
        </>
    );
}

function useIrrigationSummary(analysis) {
    const { isFetching, isLoading, data } = useAnalysisSummary(analysis);

    return {
        isFetching,
        isLoading,
        irrigation: data?.irrigation,
    };
}
